import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { deviceStatusAPI } from '../../services/api';
import { useAuthStore, useBranchStore } from '../../store';
import usePermission from '../../hooks/usePermission';
import { toast } from 'react-toastify';
import DateRangeCard from '../../components/DateRangeCard/DateRangeCard';
import DataTable from '../../components/DataTable/DataTable';
import './DeviceStatus.css';

const DEFAULT_FORM = { name: '', mac: '', ipAddress: '', gatewayMac: '' };

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
// How long ago the status last changed, in words — the time itself is shown above it.
const sinceLabel = (d) => {
    if (!d) return '';
    const mins = Math.max(0, Math.round((Date.now() - new Date(d)) / 60000));
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    if (mins < 48 * 60) return `${Math.round(mins / 60)} h ago`;
    return `${Math.round(mins / 1440)} days ago`;
};

const DeviceStatus = () => {
    const { selectedBranch } = useBranchStore();
    const { canCreate, canDelete } = usePermission('devices');

    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [range, setRange] = useState({ start: '', end: '' });
    const [selectedIds, setSelectedIds] = useState([]);

    const [showAddModal, setShowAddModal] = useState(false);
    const [formData, setFormData] = useState(DEFAULT_FORM);
    const [saving, setSaving] = useState(false);
    const [csvFile, setCsvFile] = useState(null);
    const [importing, setImporting] = useState(false);

    const fetchDevices = useCallback(async () => {
        try {
            setLoading(true);
            const params = { branchId: selectedBranch?._id };
            if (range.start) params.from = range.start;
            if (range.end) params.to = range.end;
            const res = await deviceStatusAPI.getAll(params);
            setDevices(res.data.data || []);
        } catch (error) {
            toast.error('Failed to fetch device status');
        } finally {
            setLoading(false);
        }
    }, [selectedBranch, range]);

    useEffect(() => { fetchDevices(); }, [fetchDevices]);

    // Online/offline split and the average uptime across the devices in view.
    const summary = useMemo(() => {
        const total = devices.length;
        const online = devices.filter((d) => d.status === 'online').length;
        const withPct = devices.filter((d) => typeof d.onlinePercent === 'number');
        const avgUptime = withPct.length ? Math.round(withPct.reduce((n, d) => n + d.onlinePercent, 0) / withPct.length) : 0;
        return { total, online, offline: total - online, avgUptime };
    }, [devices]);

    function openAddModal() {
        setFormData({ ...DEFAULT_FORM });
        setCsvFile(null);
        setShowAddModal(true);
    }

    async function handleSave(e) {
        e.preventDefault();
        if (!formData.name.trim() || !formData.mac.trim()) {
            toast.warning('Device name and MAC address are required');
            return;
        }
        setSaving(true);
        try {
            await deviceStatusAPI.create({ ...formData, branchId: selectedBranch?._id });
            toast.success('Device added');
            setShowAddModal(false);
            fetchDevices();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to add device');
        } finally {
            setSaving(false);
        }
    }

    async function handleImportCsv() {
        if (!csvFile) {
            toast.warning('Choose a CSV file first');
            return;
        }
        setImporting(true);
        try {
            const res = await deviceStatusAPI.importCsv(csvFile, selectedBranch?._id);
            const { created, skipped } = res.data;
            if (skipped?.length) {
                toast.warning(`Imported ${created} device(s), skipped ${skipped.length} row(s) — check they have a name and a valid MAC`);
            } else {
                toast.success(`Imported ${created} device(s)`);
            }
            setCsvFile(null);
            setShowAddModal(false);
            fetchDevices();
        } catch (error) {
            toast.error(error.response?.data?.message || 'CSV import failed');
        } finally {
            setImporting(false);
        }
    }

    async function handleDownloadTemplate() {
        try {
            const token = useAuthStore.getState().token;
            const res = await fetch(deviceStatusAPI.templateUrl(), { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error('Download failed');
            const blob = await res.blob();
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = 'device-status-template.csv';
            a.click();
            URL.revokeObjectURL(a.href);
        } catch (error) {
            toast.error('Failed to download template');
        }
    }

    async function handleDeleteSelected() {
        if (selectedIds.length === 0) return;
        if (!window.confirm(`Delete ${selectedIds.length} selected device(s)?`)) return;
        try {
            await deviceStatusAPI.bulkDelete(selectedIds);
            toast.success('Selected devices deleted');
            setSelectedIds([]);
            fetchDevices();
        } catch (error) {
            toast.error('Failed to delete selected devices');
        }
    }

    async function handleDeleteAll() {
        if (devices.length === 0) return;
        if (!window.confirm(`Delete ALL ${devices.length} device(s)? This cannot be undone.`)) return;
        try {
            await deviceStatusAPI.deleteAll(selectedBranch?._id);
            toast.success('All devices deleted');
            setSelectedIds([]);
            fetchDevices();
        } catch (error) {
            toast.error('Failed to delete all devices');
        }
    }

    const columns = [
        { label: 'Device Name', field: 'name', render: (r) => <span className="ds-name">{r.name}</span> },
        { label: 'MAC ID', field: 'mac', render: (r) => <code className="ds-mac">{r.mac}</code> },
        { label: 'IP Address', field: 'ip', render: (r) => (r.ip === '—' ? <span className="ds-muted">—</span> : <code className="ds-mac">{r.ip}</code>) },
        {
            label: 'Status', field: 'status',
            filter: { options: ['Online', 'Offline'] },
            searchValue: (r) => (r.status === 'online' ? 'Online' : 'Offline'),
            render: (r) => (
                <span className={`status-badge ${r.status === 'online' ? 'active' : 'inactive'}`}>
                    <span className="badge-dot"></span>{r.status === 'online' ? 'Online' : 'Offline'}
                </span>
            ),
        },
        {
            label: 'Uptime', field: 'onlinePercent', searchValue: (r) => r.onlinePercent,
            render: (r) => (
                <span className="ds-uptime" title={r.checksTotal ? `Seen on ${r.checksOnline} of ${r.checksTotal} checks` : 'No checks recorded yet'}>
                    <span className="ds-meter" aria-hidden="true"><span className="ds-meter-fill" style={{ width: `${Math.max(0, Math.min(100, r.onlinePercent))}%` }}></span></span>
                    <span className="ds-uptime-pct">{r.onlinePercent}%</span>
                </span>
            ),
        },
        {
            label: 'Last status changed', field: 'lastChangedSort', searchValue: (r) => r.lastChangedText,
            render: (r) => (
                <span className="ds-when">
                    <span>{r.lastChangedText}</span>
                    {r.lastChangedAgo && <small>{r.lastChangedAgo}</small>}
                </span>
            ),
        },
    ];

    const rows = devices.map((d) => ({
        id: d._id,
        name: d.name,
        mac: d.mac,
        ip: d.ipAddress || '—',
        status: d.status,
        onlinePercent: d.onlinePercent ?? 0,
        checksTotal: d.checksTotal,
        checksOnline: d.checksOnline,
        lastChangedText: fmtDateTime(d.lastStatusChangedAt),
        lastChangedAgo: sinceLabel(d.lastStatusChangedAt),
        lastChangedSort: d.lastStatusChangedAt ? new Date(d.lastStatusChangedAt).getTime() : 0,
    }));

    const tiles = [
        { label: 'Total devices', value: summary.total, icon: 'fa-network-wired', tone: 'navy' },
        { label: 'Online', value: summary.online, icon: 'fa-circle-check', tone: 'green' },
        { label: 'Offline', value: summary.offline, icon: 'fa-circle-xmark', tone: 'red' },
        { label: 'Average uptime', value: `${summary.avgUptime}%`, icon: 'fa-gauge-high', tone: 'navy' },
    ];

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <i className="fas fa-laptop"></i>
                            Device Status
                        </h1>
                        <p className="page-subtitle-modern">Network devices (switches, APs, gateways) tracked for online / offline status</p>
                    </div>
                    {canCreate && (
                        <div className="ds-header-actions">
                            <button className="btn-primary-modern" onClick={openAddModal}>
                                <i className="fas fa-plus"></i> Add Device
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="ds-tiles">
                {tiles.map((t) => (
                    <div key={t.label} className="ds-tile">
                        <div className={`ds-tile-icon ds-tone-${t.tone}`}><i className={`fas ${t.icon}`}></i></div>
                        <div className="ds-tile-body">
                            <span className="ds-tile-value">{loading ? '—' : t.value}</span>
                            <span className="ds-tile-label">{t.label}</span>
                        </div>
                    </div>
                ))}
            </div>

            <DateRangeCard value={range} onApply={setRange} onClear={() => setRange({ start: '', end: '' })} />

            <div className="ad-card" style={{ marginTop: '1.5rem' }}>
                <div className="ad-card-header">
                    <div className="ad-card-title">
                        <i className="fas fa-network-wired"></i>
                        <h2>Devices</h2>
                    </div>
                    {canDelete && (
                        <div className="ds-table-actions">
                            {selectedIds.length > 0 && (
                                <button className="btn-danger-modern btn-sm" onClick={handleDeleteSelected}>
                                    <i className="fas fa-trash"></i> Delete {selectedIds.length} selected
                                </button>
                            )}
                            <button className="btn-secondary-modern btn-sm" onClick={handleDeleteAll} disabled={devices.length === 0}>
                                <i className="fas fa-trash-alt"></i> Delete all
                            </button>
                        </div>
                    )}
                </div>
                <div style={{ padding: '1.125rem 1.375rem' }}>
                    {loading ? (
                        <div className="loading-modern"><div className="spinner-modern"></div><p>Loading devices...</p></div>
                    ) : (
                        <DataTable
                            selectable={canDelete}
                            selectedIds={selectedIds}
                            onSelectionChange={setSelectedIds}
                            columns={columns}
                            rows={rows}
                            emptyMessage="No devices tracked yet — add one, or import a CSV"
                        />
                    )}
                </div>
            </div>

            {showAddModal && (
                <div className="modal-overlay-modern" onClick={() => setShowAddModal(false)}>
                    <div className="modal-modern" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2>Add New Device</h2>
                            <button className="modal-close" onClick={() => setShowAddModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <form onSubmit={handleSave}>
                            <div className="modal-body-modern">
                                <div className="form-group-modern">
                                    <label>Device Name</label>
                                    <input type="text" value={formData.name} onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))} required />
                                </div>
                                <div className="form-group-modern">
                                    <label>MAC Address</label>
                                    <input type="text" placeholder="AA:BB:CC:DD:EE:FF" value={formData.mac} onChange={(e) => setFormData((f) => ({ ...f, mac: e.target.value }))} required />
                                </div>
                                <div className="form-group-modern">
                                    <label>IP Address</label>
                                    <input type="text" value={formData.ipAddress} onChange={(e) => setFormData((f) => ({ ...f, ipAddress: e.target.value }))} />
                                </div>
                                <div className="form-group-modern">
                                    <label>Gateway MAC</label>
                                    <input type="text" placeholder="AA:BB:CC:DD:EE:FF" value={formData.gatewayMac} onChange={(e) => setFormData((f) => ({ ...f, gatewayMac: e.target.value }))} />
                                </div>
                            </div>
                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={() => setShowAddModal(false)}>Cancel</button>
                                <button type="submit" className="btn-primary-modern" disabled={saving}>{saving ? 'Saving...' : 'Save Device'}</button>
                            </div>
                        </form>

                        <div className="device-status-import">
                            <div className="form-group-modern">
                                <label>Import Devices (CSV)</label>
                                <input type="file" accept=".csv" onChange={(e) => setCsvFile(e.target.files?.[0] || null)} />
                                <p className="input-hint">Format: device_name,mac_address,ip_address,gateway_mac</p>
                            </div>
                            <div className="device-status-import-actions">
                                <button type="button" className="btn-ghost" onClick={handleDownloadTemplate}>
                                    <i className="fas fa-download"></i> Download CSV Template
                                </button>
                                <button type="button" className="btn-primary-modern" onClick={handleImportCsv} disabled={importing}>
                                    {importing ? 'Importing...' : 'Import CSV'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeviceStatus;
